import { Button, Flexbox, Text } from '@morf/ui';
import { FC, memo } from 'react';
import { StyledAvailabilitySlot } from './AvailabilitySlot.css';
import { formatTimestampTo12HourTime } from '../../../helpers/formatTimestampTo12HourTime';
import { useConfirmTimeSlot } from '../../../helpers/useConfirmTimeSlot';
import { useSchedule } from '../../../context/schedule/useSchedule';
import { useTheme } from '@morf/theming';
import {
  AvailabilitySlot as AvailabilitySlotType,
  AvailabilitySlotProps,
} from './types';

const AvailabilitySlot: FC<AvailabilitySlotProps> = ({
  availabilitySlot,
  isLoading,
}) => {
  const theme = useTheme();

  const {
    schedulingTimezone,
    selectedAvailabilitySlot,
    setSelectedAvailabilitySlot,
  } = useSchedule();

  const { isConfirming, handleConfirmSlot } =
    useConfirmTimeSlot(availabilitySlot);

  const formatAvailabilitySlot = (slot: AvailabilitySlotType | undefined) => {
    return `${slot?.providerId?.id}-${slot?.providerId?.idType}-${slot?.slot?.seconds?.low}`;
  };

  const formattedAvailabilitySlot = formatAvailabilitySlot(availabilitySlot);

  const isSelected =
    formatAvailabilitySlot(selectedAvailabilitySlot) ===
    formattedAvailabilitySlot;

  const formattedAvailabilitySlotTo12HourTime = formatTimestampTo12HourTime(
    availabilitySlot.slot,
    schedulingTimezone
  );

  const handleSelectSlot = () => {
    setSelectedAvailabilitySlot(availabilitySlot);
  };

  return (
    <Flexbox gap={0.25} key={formatAvailabilitySlot(availabilitySlot)}>
      <StyledAvailabilitySlot
        data-testid='availability-slot'
        isActive={isSelected}
        isLoading={isLoading}
        onClick={handleSelectSlot}
        shape='square'
      >
        <Text tag='h5' color='inherit'>
          {formattedAvailabilitySlotTo12HourTime}
        </Text>
      </StyledAvailabilitySlot>

      {isSelected && (
        <Button
          data-testid='confirm'
          variant={theme.confirmButton ? 'custom' : 'primary'}
          {...(theme.confirmButton && {
            backgroundColor: theme.confirmButton.backgroundColor,
            color: theme.confirmButton.color,
            borderColor: theme.confirmButton.backgroundColor,
            hoverBackgroundColor: theme.confirmButton.hoverBackgroundColor,
            hoverColor: theme.confirmButton.hoverColor,
            hoverBorderColor: theme.confirmButton.hoverBackgroundColor,
          })}
          onClick={handleConfirmSlot}
          isDisabled={isConfirming}
          borderRadius={theme.timePicker.borderRadius}
          shape='square'
          width='100%'
        >
          <Text tag='h5' color='inherit'>
            {isConfirming ? 'Confirming...' : 'Confirm'}
          </Text>
        </Button>
      )}
    </Flexbox>
  );
};

export const MemoizedAvailabilitySlot = memo(AvailabilitySlot);
